import React from 'react';
import { Button, Tooltip } from 'antd';
import { ECoverageStatus, EEligibilityOutcome, TEligibilityCheckVo } from 'types/insuranceEligibility';
import { barScene, formatPlanDate, payerProse } from '../derive';
import {
    BAR_COPY,
    PLAN_TYPE_OON_BAR,
    BASIS_COPY,
    DISCLAIMER_BOLD,
    DISCLAIMER_LEAD,
    DISCLAIMER_TAIL,
    RECHECK_COPY,
    SYSTEM_ERROR_COPY,
    NO_COVERAGE_DETAIL_COPY,
} from '../copy';
import { TAaaEntry, mergeRejection } from '../aaa';
import LastCheckedLine from './LastCheckedLine';
import CostTiles from './CostTiles';
import BhBlock from './BhBlock';
import InfoLines from './InfoLines';
import BillingFooter from './BillingFooter';
import RejectedBody from './RejectedBody';
import AwaitStrip from './AwaitStrip';
import s from '../s.module.less';

// 实心图标（原型 CHECK_ICON / WARN_ICON / CROSS_ICON）：填充色 + 白色笔画，非描边轮廓
const ICONS = {
    ok: (
        <svg viewBox="0 0 20 20" fill="none">
            <circle cx="10" cy="10" r="10" fill="#34D399" />
            <path
                d="M6 10.5l2.5 2.5L14 7.5"
                stroke="#fff"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    ),
    problem: (
        <svg viewBox="0 0 20 20" fill="none">
            <path
                d="M9.14 2.88a1 1 0 0 1 1.72 0l8.14 14.12a1 1 0 0 1-.86 1.5H1.86a1 1 0 0 1-.86-1.5z"
                fill="currentColor"
            />
            <path d="M10 7.4v4" stroke="#fff" strokeWidth="1.9" strokeLinecap="round" />
            <circle cx="10" cy="14.7" r="1.05" fill="#fff" />
        </svg>
    ),
    bad: (
        <svg viewBox="0 0 20 20" fill="none">
            <circle cx="10" cy="10" r="10" fill="currentColor" />
            <path d="M6.9 6.9l6.2 6.2M13.1 6.9l-6.2 6.2" stroke="#fff" strokeWidth="1.9" strokeLinecap="round" />
        </svg>
    ),
};

type RecheckState = {
    // 从未查过不渲染
    visible: boolean;
    disabled: boolean;
    tip?: string;
    onClick: () => void;
};

type Props = {
    check: TEligibilityCheckVo;
    // 档案上的 carrier 拼写：payerProse 优先用它（provider 在本屏其他地方看到的就是它）
    carrier: string;
    persona: 'managed' | 'manual';
    skipCostModal: boolean;
    foldOpen: boolean;
    onToggleFold: () => void;
    recheck: RecheckState;
    // 拒识修复卡的上下文与动作（AAA 流）
    clientId: number;
    memberIdOnFile?: string;
    cardRequestedAt?: string;
    // 二次失败时替换修复卡的 Recommended fix：按字段的指引已被试过且失败，改说 deadline
    secondFailFix?: string;
    onRequestCard: (aaa?: TAaaEntry) => void;
    onMessagePatient: (aaa?: TAaaEntry) => void;
    // plan type 不在 template 的 OON（10/05）：template 归谁 + 补 template 入口（practice 视图不给）
    templateWhose?: string;
    onAddToTemplate?: () => void;
    // 10/21：顶条不再带 carrier（太长）；改在 Last checked 下一行点名这张是哪张、主还是次
    planScope?: string;
    // 多份 primary（10/05）：可用结果落在非 default 上时的 Set as default 行。原型 defaultRow：
    // 紧跟 Last checked、在 plan 行之前（它回答的是「这张是谁」，先于价格）
    setDefault?: React.ReactNode;
    // 多份 primary（10/05）：失败卡上的「查另一张」盒。容器按条件算好再传，卡片只管放在动作之后、计费脚注之前
    extra?: React.ReactNode;
};

// 所有状态一个形状：一条摘要 bar + 折叠 body。默认折叠，只有主动 check 后展开一次
const ResultCard: React.FC<Props> = ({
    check,
    carrier,
    persona,
    skipCostModal,
    foldOpen,
    onToggleFold,
    recheck,
    clientId,
    memberIdOnFile,
    cardRequestedAt,
    secondFailFix,
    onRequestCard,
    onMessagePatient,
    templateWhose,
    onAddToTemplate,
    planScope,
    setDefault,
    extra,
}) => {
    const scene = barScene(check);
    const r = check.result;
    const rejected = check.outcome === EEligibilityOutcome.REJECTED || !r;
    const inactive = r?.coverageStatus === ECoverageStatus.INACTIVE;
    // SYSTEM_ERROR 不走拒识修复卡：请求根本没发出去，没有 271 可修（PAYER_DOWN 是 payer 的
    // 「答不了」答复，仍走修复卡）。红 bad 调 + 专属 body，只在没有历史结果时整卡出现
    // （有历史结果时 hook 层根本不会把失败落成 latest）
    const systemError = check.statusTokens?.[0] === 'SYSTEM_ERROR';
    // payer 答了 200 但没给任何 coverage 信息：琥珀调专属卡，不出 tiles / 修复盒 / disclaimer
    const noCoverageDetail = check.statusTokens?.[0] === 'NO_COVERAGE_DETAIL';
    // 拒识：bar 直接承载原因（单码 short / 多码 " + " 连接，不隐藏任何码）
    const merged = rejected && !systemError ? mergeRejection(check.rejection?.codes || []) : undefined;
    let bar: { tone: 'ok' | 'problem' | 'bad'; text: string };
    if (systemError) {
        bar = { tone: 'bad', text: SYSTEM_ERROR_COPY.barLine };
    } else if (noCoverageDetail) {
        bar = { tone: 'problem', text: NO_COVERAGE_DETAIL_COPY.barLine };
    } else if (merged) {
        bar = { tone: merged.tone, text: merged.barLine };
    } else if (scene === 'oon' && r?.network?.planTypeNotOnTemplate) {
        bar = { tone: 'bad', text: PLAN_TYPE_OON_BAR(r.network.planTypeNotOnTemplate) };
    } else {
        bar = BAR_COPY[scene];
    }

    // payer 点名（BH 块 / SNC 行）：271 名字过 payerProse，与 carrier 拼写归一。
    // 基准用被查 record 的 carrier 快照，档案值只作兜底——患者换卡后档案已是新保司，
    // 拿它去归一旧那次的 271 名字会张冠李戴
    const payerName = payerProse(r?.payer?.name, check.carrier || carrier);

    // plan 期限用数字日期（原型 fmtDate "01/01/2026 – 12/31/2026"）；无结束日说 "onwards"
    const planLine =
        r?.plan && !inactive ? (
            <div className={s.planLine}>
                {r.plan.name}
                {r.plan.startDate && (
                    <span className={s.planDates}>
                        {' · '}
                        {formatPlanDate(r.plan.startDate)}
                        {r.plan.endDate ? ` – ${formatPlanDate(r.plan.endDate)}` : ' onwards'}
                    </span>
                )}
            </div>
        ) : null;

    let body: React.ReactNode;
    if (systemError) {
        // 原型 viewSystemError：exp 一段（不带 Last checked——从未有过完成时间可标）+
        // manual 的 no-charge 脚注（managed 全程无计费文案）。没有修复盒、没有 disclaimer
        body = (
            <>
                {/* 没有 Last checked 可挂，「查的是哪张」直接放正文最上方 */}
                {planScope && <div className={s.planScope}>{planScope}</div>}
                <div className={s.infoLine}>
                    {SYSTEM_ERROR_COPY.expLead}
                    <b>{SYSTEM_ERROR_COPY.expBold}</b>
                    {SYSTEM_ERROR_COPY.expTail}
                </div>
                {extra}
                {persona === 'manual' && <div className={s.billingFoot}>{SYSTEM_ERROR_COPY.noCharge}</div>}
            </>
        );
    } else if (noCoverageDetail) {
        // 有完成时间（payer 真答了），所以带 Last checked；计费脚注同 system error 口径只给 manual
        body = (
            <>
                <LastCheckedLine checkedAt={check.checkedAt} checkedBy={check.checkedBy} planScope={planScope} />
                <div className={s.infoLine}>{NO_COVERAGE_DETAIL_COPY.body}</div>
                <div className={s.infoLine}>{NO_COVERAGE_DETAIL_COPY.next}</div>
                {extra}
                {persona === 'manual' && <div className={s.billingFoot}>{NO_COVERAGE_DETAIL_COPY.noCharge}</div>}
            </>
        );
    } else if (rejected && merged) {
        body = (
            <RejectedBody
                check={check}
                merged={merged}
                persona={persona}
                skipCostModal={skipCostModal}
                clientId={clientId}
                memberIdOnFile={memberIdOnFile}
                cardRequestedAt={cardRequestedAt}
                secondFailFix={secondFailFix}
                onRequestCard={onRequestCard}
                onMessagePatient={onMessagePatient}
                extra={extra}
                planScope={planScope}
            />
        );
    } else if (inactive) {
        // inactive：不渲染 tiles（抑制而非清零），也没有 disclaimer（没有正面结论可限定）。
        // 唯一的动作是要一张新卡（多半是换了计划）；请求已发出则换等待条
        body = (
            <>
                <LastCheckedLine checkedAt={check.checkedAt} checkedBy={check.checkedBy} planScope={planScope} />
                <InfoLines result={r} scene="inactive" />
                {cardRequestedAt ? (
                    <AwaitStrip requestedAt={cardRequestedAt} />
                ) : (
                    <div>
                        <Button type="primary" onClick={() => onRequestCard(undefined)}>
                            Request updated insurance
                        </Button>
                    </div>
                )}
                {extra}
                <BillingFooter persona={persona} skipCostModal={skipCostModal} />
            </>
        );
    } else {
        body = (
            <>
                <LastCheckedLine checkedAt={check.checkedAt} checkedBy={check.checkedBy} planScope={planScope} />
                {setDefault}
                {/* out-of-network 的警告行提到金额之前：先说资格，再说价格 */}
                {scene === 'oon' && (
                    <InfoLines
                        result={r}
                        scene="oon"
                        payerName={payerName}
                        onMessagePatient={() => onMessagePatient()}
                        templateWhose={templateWhose}
                        onAddToTemplate={onAddToTemplate}
                    />
                )}
                {/* plan 行与 tiles 是同一 section 的两个部分（8px），与其他 section 间距 16px */}
                {(planLine || r.costSharing) && (
                    <div>
                        {planLine}
                        {r.costSharing && <CostTiles costSharing={r.costSharing} />}
                        {/* 数字来源警示：GENERAL/MIXED 才显示——好情况沉默；carve-out 块在场时抑制
                            （它把同一件事说得更重：金额根本不适用） */}
                        {r.costSharing &&
                            (r.costSharing.basis === 'GENERAL' || r.costSharing.basis === 'MIXED') &&
                            !r.behavioralHealth?.carveOut && (
                                <div className={s.basisNote}>
                                    {ICONS.problem}
                                    <div>
                                        {r.costSharing.basis === 'MIXED'
                                            ? BASIS_COPY.mixed
                                            : BASIS_COPY.general(payerName)}
                                    </div>
                                </div>
                            )}
                    </div>
                )}
                {r.behavioralHealth && <BhBlock bh={r.behavioralHealth} payerName={payerName} />}
                {scene !== 'oon' && (
                    <InfoLines
                        result={r}
                        scene={scene}
                        payerName={payerName}
                        onMessagePatient={() => onMessagePatient()}
                    />
                )}
                {extra}
                <BillingFooter persona={persona} skipCostModal={skipCostModal} />
                <div className={s.disclaimer}>
                    {DISCLAIMER_LEAD}
                    <b>{DISCLAIMER_BOLD}</b>
                    {DISCLAIMER_TAIL}
                </div>
            </>
        );
    }

    const recheckBtn = recheck.visible ? (
        <Tooltip title={recheck.tip} overlayStyle={{ maxWidth: 280 }}>
            {/* disabled 的 antd Button 不触发鼠标事件，包一层 span 让 Tooltip 仍可用 */}
            <span
                onClick={(e) => {
                    e.stopPropagation();
                }}
            >
                <Button
                    size="small"
                    className={s.recheckBtn}
                    disabled={recheck.disabled}
                    onClick={() => recheck.onClick()}
                >
                    <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    >
                        <path d="M21 12a9 9 0 1 1-2.64-6.36" />
                        <polyline points="21 3 21 9 15 9" />
                    </svg>
                    {RECHECK_COPY.label}
                </Button>
            </span>
        </Tooltip>
    ) : null;

    return (
        <div className={`${s.resCard} ${foldOpen ? s.resCardOpen : ''}`}>
            <div
                className={`${s.resBar} ${s[`tone_${bar.tone}`]}`}
                onClick={onToggleFold}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                    if (e.key === 'Enter') onToggleFold();
                }}
            >
                <span className={s.resLine}>
                    {/* 拒识卡即使在琥珀 tone 下也用叉号（原型 viewRejected 恒传 CROSS_ICON）*/}
                    <span className={s.resIcon}>{merged ? ICONS.bad : ICONS[bar.tone]}</span>
                    <span>{bar.text}</span>
                </span>
                <span className={s.resActs}>
                    {recheckBtn}
                    <button
                        type="button"
                        className={`${s.foldToggle} ${foldOpen ? s.foldToggleOpen : ''}`}
                        onClick={(e) => {
                            e.stopPropagation();
                            onToggleFold();
                        }}
                    >
                        {foldOpen ? 'Less' : 'Details'}
                        <svg
                            viewBox="0 0 20 20"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        >
                            <polyline points="6 8 10 12 14 8" />
                        </svg>
                    </button>
                </span>
            </div>
            <div className={`${s.resFold} ${foldOpen ? s.resFoldOpen : ''}`}>
                <div className={s.resFoldIn}>
                    <div className={s.cardBody}>{body}</div>
                </div>
            </div>
        </div>
    );
};

export default ResultCard;
